import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Container } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faBrain,
  faBolt,
  faFaceSmile,
  faChartLine,
  faWind,
  faClipboardCheck,
  faHandHoldingHeart,
  faArrowRight,
  faArrowUp,
  faArrowDown,
  faFire,
  faGauge,
  faSpa,
  faSpinner,
  faExclamationTriangle,
} from '@fortawesome/free-solid-svg-icons';
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from 'recharts';
import type { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import mentalWellnessService, {
  DashboardData,
  MentalHealthScore,
  MoodStats,
  TeamComparison,
} from '../../services/mentalWellnessService';
import BackButton from '../../components/BackButton/BackButton';
import { useProfile } from '../../context/ProfileContext';
import './MentalWellness.css';
import './Dashboard/MentalWellnessDashboard.css';

/* ------------------------------------------------------------------ */
/*  HELPERS                                                            */
/* ------------------------------------------------------------------ */

const getScoreColor = (cat: string) => cat === 'green' ? '#10b981' : cat === 'amber' ? '#f59e0b' : cat === 'red' ? '#ef4444' : '#94a3b8';
const getScoreLabel = (cat: string) => cat === 'green' ? 'Good' : cat === 'amber' ? 'Fair' : cat === 'red' ? 'Needs Attention' : 'Unknown';
const formatDate = (iso: string) => { if (!iso) return ''; const d = new Date(iso); return d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short' }); };

/* ------------------------------------------------------------------ */
/*  FEATURE CARDS (4 cards — dashboard removed)                        */
/* ------------------------------------------------------------------ */

interface MwFeatureCard {
  id: string;
  icon: IconDefinition;
  title: string;
  description: string;
  route: string;
  color: string;
  lightBg: string;
}

const featureCards: MwFeatureCard[] = [
  {
    id: 'mood-checkin',
    icon: faFaceSmile,
    title: 'Daily Mood Check-in',
    description: 'Track your daily mood, energy and stress levels to discover patterns.',
    route: '/mental-wellness/mood-checkin',
    color: '#7c3aed',
    lightBg: '#f5f3ff',
  },
  {
    id: 'breathing',
    icon: faWind,
    title: 'Guided Breathing',
    description: 'Calm your mind with Box Breathing, Deep Breathing and the 4-7-8 technique.',
    route: '/mental-wellness/breathing',
    color: '#06b6d4',
    lightBg: '#ecfeff',
  },
  {
    id: 'screening',
    icon: faClipboardCheck,
    title: 'Mental Health Screening',
    description: 'Take PHQ-9 depression or GAD-7 anxiety screening with instant results.',
    route: '/mental-wellness/screening',
    color: '#10b981',
    lightBg: '#ecfdf5',
  },
  {
    id: 'action-plan',
    icon: faHandHoldingHeart,
    title: 'Self-Care Plan',
    description: 'Get personalized recommendations for your wellbeing and track progress.',
    route: '/mental-wellness/action-plan',
    color: '#f59e0b',
    lightBg: '#fffbeb',
  },
];

/* ------------------------------------------------------------------ */
/*  COMPONENT                                                          */
/* ------------------------------------------------------------------ */

const MentalWellness: React.FC = () => {
  const { t } = useTranslation(['common']);
  const { activeProfile } = useProfile();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [dashboard, setDashboard] = useState<DashboardData | null>(null);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await mentalWellnessService.getDashboard();
      setDashboard(data);
    } catch (err) {
      setError('Failed to load dashboard data.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData, activeProfile]);

  /* ================================================================ */
  /*  Dashboard Renders                                                */
  /* ================================================================ */

  const renderScoreCircle = (score: MentalHealthScore) => {
    const color = getScoreColor(score.category);
    const circumference = 2 * Math.PI * 54;
    const progress = (score.score / 100) * circumference;

    return (
      <div className="mw-db-score-card">
        <div className="mw-db-score-circle-wrap">
          <svg width="140" height="140" viewBox="0 0 120 120">
            <circle cx="60" cy="60" r="54" fill="none" stroke="#f1f5f9" strokeWidth="8" />
            <circle cx="60" cy="60" r="54" fill="none" stroke={color} strokeWidth="8"
              strokeDasharray={circumference} strokeDashoffset={circumference - progress}
              strokeLinecap="round" transform="rotate(-90 60 60)"
              style={{ transition: 'stroke-dashoffset 1s ease' }}
            />
          </svg>
          <div className="mw-db-score-value" style={{ color }}>{score.score}</div>
        </div>
        <div className="mw-db-score-info">
          <h3 className="mw-db-score-title">Mental Health Score</h3>
          <span className="mw-db-score-badge" style={{ background: `${color}15`, color, borderColor: `${color}30` }}>
            {getScoreLabel(score.category)}
          </span>
        </div>
        <div className="mw-db-breakdown">
          {Object.entries(score.breakdown).map(([key, val]) => (
            <div className="mw-db-breakdown-item" key={key}>
              <div className="mw-db-breakdown-header">
                <span className="mw-db-breakdown-label">{key.charAt(0).toUpperCase() + key.slice(1)}</span>
                <span className="mw-db-breakdown-value">{val}/100</span>
              </div>
              <div className="mw-db-breakdown-bar">
                <div className="mw-db-breakdown-fill" style={{ width: `${val}%`, background: color, transition: 'width 0.8s ease' }} />
              </div>
            </div>
          ))}
        </div>
        {score.nudges && score.nudges.length > 0 && (
          <div className="mw-db-nudges">
            {score.nudges.map((nudge, i) => (
              <div className="mw-db-nudge" key={i}>
                <FontAwesomeIcon icon={faHandHoldingHeart} />
                <span>{nudge}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  const renderStatsRow = (stats: MoodStats, streak: any) => {
    const statCards = [
      { label: 'Avg Mood', value: stats.avg_mood?.toFixed(1) || '--', icon: faFaceSmile, color: '#7c3aed', suffix: '/5' },
      { label: 'Avg Energy', value: stats.avg_energy?.toFixed(1) || '--', icon: faBolt, color: '#06b6d4', suffix: '/5' },
      { label: 'Avg Stress', value: stats.avg_stress?.toFixed(1) || '--', icon: faGauge, color: '#f59e0b', suffix: '/5' },
      { label: 'Current Streak', value: String(streak?.current_streak || streak || 0), icon: faFire, color: '#ef4444', suffix: ' days' },
    ];

    return (
      <div className="mw-db-stats-row">
        {statCards.map((stat, idx) => (
          <div className="mw-db-stat-card" key={idx}>
            <div className="mw-db-stat-icon-wrap" style={{ background: `${stat.color}12`, color: stat.color }}>
              <FontAwesomeIcon icon={stat.icon} />
            </div>
            <div className="mw-db-stat-text">
              <span className="mw-db-stat-num">{stat.value}<small>{stat.suffix}</small></span>
              <span className="mw-db-stat-label">{stat.label}</span>
            </div>
          </div>
        ))}
      </div>
    );
  };

  const renderMoodChart = (trends: { date: string; mood: number }[]) => {
    const chartData = trends.map((t) => ({ date: formatDate(t.date), mood: t.mood }));
    return (
      <div className="mw-db-chart-card">
        <h3 className="mw-db-card-title"><FontAwesomeIcon icon={faChartLine} /> Mood Trends</h3>
        {chartData.length > 0 ? (
          <div className="mw-db-chart-wrap">
            <ResponsiveContainer width="100%" height={240}>
              <LineChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                <XAxis dataKey="date" tick={{ fontSize: 11, fill: '#94a3b8' }} />
                <YAxis domain={[0, 5]} tick={{ fontSize: 11, fill: '#94a3b8' }} />
                <Tooltip contentStyle={{ borderRadius: 12, border: 'none', boxShadow: '0 4px 16px rgba(0,0,0,0.1)', fontFamily: 'Nunito', fontSize: '0.85rem' }} />
                <Line type="monotone" dataKey="mood" stroke="#7c3aed" strokeWidth={2.5} dot={{ fill: '#7c3aed', r: 4 }} activeDot={{ r: 6, fill: '#7c3aed' }} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        ) : (
          <div className="mw-db-chart-empty"><p>No mood data yet. Start logging your daily mood to see trends.</p></div>
        )}
      </div>
    );
  };

  const renderTeamComparison = (tc: TeamComparison | undefined) => {
    if (!tc) return null;
    if (tc.insufficient_data) {
      return (
        <div className="mw-db-team-card">
          <h3 className="mw-db-card-title"><FontAwesomeIcon icon={faChartLine} /> You vs. Your Organization</h3>
          <p className="mw-db-team-insufficient">
            Team comparison requires at least {tc.min_participants || 5} participants. Currently {tc.current_participants || 0} colleagues have logged their mood.
          </p>
        </div>
      );
    }
    const metrics = [
      { label: 'Mood', key: 'mood' as const, icon: faFaceSmile, color: '#8b5cf6' },
      { label: 'Energy', key: 'energy' as const, icon: faBolt, color: '#f59e0b' },
      { label: 'Sleep', key: 'sleep' as const, icon: faBrain, color: '#6366f1' },
      { label: 'Stress', key: 'stress' as const, icon: faGauge, color: '#ef4444' },
    ];
    return (
      <div className="mw-db-team-card">
        <h3 className="mw-db-card-title">
          <FontAwesomeIcon icon={faChartLine} /> You vs. Your Organization
          <span className="mw-db-team-participants">{tc.participants} colleagues</span>
        </h3>
        <div className="mw-db-team-grid">
          {metrics.map((m) => {
            const yours = tc.your_averages?.[m.key];
            const team = tc.team_averages?.[m.key];
            const diff = yours != null && team != null ? +(yours - team).toFixed(1) : null;
            const isStress = m.key === 'stress';
            const isBetter = diff != null ? (isStress ? diff < 0 : diff > 0) : null;
            return (
              <div className="mw-db-team-metric" key={m.key}>
                <div className="mw-db-team-metric-icon" style={{ background: `${m.color}15`, color: m.color }}>
                  <FontAwesomeIcon icon={m.icon} />
                </div>
                <div className="mw-db-team-metric-label">{m.label}</div>
                <div className="mw-db-team-metric-values">
                  <div className="mw-db-team-metric-yours">
                    <span className="mw-db-team-metric-value">{yours != null ? yours.toFixed(1) : '—'}</span>
                    <span className="mw-db-team-metric-tag">You</span>
                  </div>
                  <div className="mw-db-team-metric-vs">vs</div>
                  <div className="mw-db-team-metric-team">
                    <span className="mw-db-team-metric-value">{team != null ? team.toFixed(1) : '—'}</span>
                    <span className="mw-db-team-metric-tag">Team Avg</span>
                  </div>
                </div>
                {diff != null && (
                  <div className={`mw-db-team-metric-diff ${isBetter ? 'better' : 'worse'}`}>
                    <FontAwesomeIcon icon={isBetter ? faArrowUp : faArrowDown} />
                    {Math.abs(diff)} {isBetter ? 'above' : 'below'} average
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  /* ================================================================ */
  /*  Main Render                                                      */
  /* ================================================================ */

  return (
    <div className="mw-page">
      {/* Hero Banner */}
      <section className="mw-welcome-banner">
        <div className="mw-welcome-circle mw-welcome-circle-1" />
        <div className="mw-welcome-circle mw-welcome-circle-2" />
        <Container>
          <BackButton />
          <div className="mw-welcome-layout">
            <div className="mw-welcome-text">
              <h1 className="mw-welcome-title">
                <FontAwesomeIcon icon={faBrain} /> Mental Wellness
              </h1>
              <p className="mw-welcome-subtitle">
                Your journey to mental wellbeing starts here. Track your mood, practice mindfulness,
                screen for concerns, and build a self-care routine that works for you.
              </p>
            </div>
          </div>
        </Container>
      </section>

      {/* Data-driven Stats Row */}
      {!loading && dashboard && (
        <section className="mw-stats-section">
          <Container>
            <div className="mw-stats-row">
              {[
                { icon: faFaceSmile, color: '#7c3aed', value: String(dashboard.mood_stats?.total_entries || 0), label: 'Mood Check-ins' },
                { icon: faFire, color: '#ef4444', value: `${dashboard.streak || 0} days`, label: 'Current Streak' },
                { icon: faWind, color: '#06b6d4', value: String(dashboard.breathing_stats?.total_sessions || 0), label: 'Breathing Sessions' },
                { icon: faHandHoldingHeart, color: '#f59e0b', value: String(dashboard.recommendations_count || 0), label: 'Active Recommendations' },
              ].map((stat, idx) => (
                <div className="mw-stat-card" key={idx}>
                  <div className="mw-stat-icon" style={{ background: `${stat.color}12`, color: stat.color }}>
                    <FontAwesomeIcon icon={stat.icon} />
                  </div>
                  <div className="mw-stat-info">
                    <span className="mw-stat-value">{stat.value}</span>
                    <span className="mw-stat-label">{stat.label}</span>
                  </div>
                </div>
              ))}
            </div>
          </Container>
        </section>
      )}

      <Container>
        {/* ── Dashboard Section ──────────────────────────────────────── */}
        {loading ? (
          <div className="mw-db-loading">
            <FontAwesomeIcon icon={faSpinner} spin size="2x" />
            <p>Loading your wellness data...</p>
          </div>
        ) : error ? (
          <div className="mw-db-error">
            <FontAwesomeIcon icon={faExclamationTriangle} />
            <p>{error}</p>
            <button className="mw-db-retry-btn" onClick={fetchData}>Try Again</button>
          </div>
        ) : dashboard ? (
          <div className="mw-db-content">
            {/* Score + Stats */}
            <div className="mw-db-top-grid">
              <div className="mw-db-top-left">
                {dashboard.score_card ? renderScoreCircle(dashboard.score_card as MentalHealthScore) : (
                  <div className="mw-db-score-card">
                    <div className="mw-db-score-empty">
                      <FontAwesomeIcon icon={faBrain} />
                      <p>Complete mood check-ins and screenings to generate your mental health score.</p>
                    </div>
                  </div>
                )}
              </div>
              <div className="mw-db-top-right">
                {renderStatsRow(
                  dashboard.mood_stats || { avg_mood: 0, avg_energy: 0, avg_sleep: 0, avg_stress: 0, total_entries: 0 },
                  dashboard.streak || 0,
                )}
              </div>
            </div>

            {/* Mood Trends */}
            {renderMoodChart(dashboard.mood_trends || [])}

            {/* Team Comparison */}
            {renderTeamComparison(dashboard.team_comparison)}
          </div>
        ) : null}

        {/* ── Feature Cards ──────────────────────────────────────────── */}
        <div className="mw-section-header">
          <div className="mw-section-header-text">
            <h2 className="mw-section-title">
              <FontAwesomeIcon icon={faSpa} /> Your Wellness Toolkit
            </h2>
            <p className="mw-section-subtitle">
              Explore tools designed to support your mental health journey.
            </p>
          </div>
        </div>

        <div className="mw-features-grid">
          {featureCards.map((card, idx) => (
            <div
              className="mw-feature-card"
              key={card.id}
              style={{ animationDelay: `${idx * 0.08}s` }}
            >
              <div className="mw-feature-icon" style={{ background: `linear-gradient(135deg, ${card.lightBg}, ${card.color}15)` }}>
                <FontAwesomeIcon icon={card.icon} style={{ color: card.color }} />
              </div>
              <h3 className="mw-feature-title">{card.title}</h3>
              <p className="mw-feature-desc">{card.description}</p>
              <button
                className="mw-explore-btn"
                style={{ background: `linear-gradient(135deg, ${card.color}, ${card.color}cc)` }}
                onClick={() => { navigate(card.route); window.scrollTo(0, 0); }}
              >
                Explore <FontAwesomeIcon icon={faArrowRight} />
              </button>
            </div>
          ))}
        </div>

        {/* Motivational Callout */}
        <div className="mw-callout-card">
          <div className="mw-callout-icon"><FontAwesomeIcon icon={faFire} /></div>
          <div className="mw-callout-text">
            <h4>Start Your Streak Today</h4>
            <p>Log your mood daily to build a streak and unlock insights about your mental wellbeing patterns.</p>
          </div>
          <button className="mw-callout-btn" onClick={() => { navigate('/mental-wellness/mood-checkin'); window.scrollTo(0, 0); }}>
            Check In Now <FontAwesomeIcon icon={faArrowRight} />
          </button>
        </div>
      </Container>
    </div>
  );
};

export default MentalWellness;
